import { useAuthStore } from '@/stores/auth.js'
import { useConfiguredServicesStore } from '@/stores/configuredServices.js'
import { getHttpClient, HTTP_CONTENT_TYPES } from '@/utilities/httpClients'
import { internalToValue } from '@/helpers'
import { CPQ_LITE_CLIENT } from '@/Globals'

function handleCpqLiteError(requestConfig, error) {
  console.error(`Failed to complete a ${requestConfig.method ?? 'get'} CPQLite request to ${requestConfig.url}`, error)
  throw error
}

/**
 * Helper function for making requests to CPQLite. Automatically handles refreshing an expired session.
 * @param {object} requestConfig Axios config object for making the API request
 * @returns Raw Axios response object
 * @throws Standard error if request fails (even after re-authentication for 401 errors)
 */
export async function callCpqLite(requestConfig) {
  const authStore = useAuthStore()

  // Ensure the user is logged into CPQLite before proceeding
  if (authStore.cpqLiteJwt === null) {
    await authStore.cpqLiteLoginCallback.promise
  }

  try {
    return await getHttpClient(CPQ_LITE_CLIENT).request(requestConfig)
  } catch (error) {
    // If the error is not an authentication error, log it and throw.
    if (error.response?.status !== 401) {
      handleCpqLiteError(requestConfig, error)
    }
  }

  // Try re-authenticating and making the request again.
  try {
    await authStore.logIntoCpqLite(authStore.jwt)

    return await getHttpClient(CPQ_LITE_CLIENT).request(requestConfig)
  } catch (error) {
    // If the error is still an authentication error, the megaport token is expired. Log the user out.
    if (error.response?.status === 401) {
      authStore.handleSessionTimeout()
    }
    handleCpqLiteError(requestConfig, error)
  }
}

/**
 * Retrieves a quote from CPQLite.
 * @param {string} quoteId UUID of the quote to be retrieved.
 * @param {boolean} withSideEffects Whether to update the configured services store with the quote data.
 * @returns The quote object from CPQLite.
 */
export async function loadQuote(quoteId, withSideEffects = true) {
  const quote = await callCpqLite({ method: 'get', url: `projects/${quoteId}` })

  if (withSideEffects) {
    const configuredServicesStore = useConfiguredServicesStore()

    configuredServicesStore.clearConfigurations()
    configuredServicesStore.populateQuoteFromApi(quote)
  }

  return quote
}

/**
 * Retrieves a list of quotes from CPQLite.
 * @param {object} queryParams Object containing query parameters to filter/sort the quotes by.
 * @returns An array of quote objects from CPQLite.
 */
export async function loadQuotes(queryParams) {
  const params = {}

  for (const param in queryParams) {
    params[param] = internalToValue(queryParams[param])
  }

  const quoteData = await callCpqLite({ method: 'get', url: 'projects', params })

  return quoteData
}

/**
 * Duplicates the given quote in CPQLite.
 * @param {string} id UUID of the quote to be duplicated.
 * @param {string} [companyUid] Company UID when duplicating a quote owned by another company.
 */
export async function duplicateQuote(id, companyUid) {
  await callCpqLite({
    method: 'post',
    url: `projects/${id}/duplicate`,
    data: companyUid ? { companyUid } : undefined,
  })
}

/**
 * Deletes a quote from CPQLite
 * @param {string} id UUID of the quote to be deleted
 */
export async function deleteQuote(id) {
  await callCpqLite({ method: 'delete', url: `projects/${id}` })
}

/**
 * Downloads a CSV version of the given quote, returning it in blob format.
 * @param {string} id UUID of the quote to be downloaded
 * @returns a Blob object containing the CSV data
 */
export async function downloadQuoteCsv(id) {
  const requestConfig = {
    method: 'get',
    url: `projects/${id}`,
    headers: { Accept: HTTP_CONTENT_TYPES.CSV },
    responseType: 'text',
  }

  const blobParts = await callCpqLite(requestConfig)

  return new Blob([blobParts])
}
